import {
  Lightbulb,
  Calendar,
  TrendingUp,
  MessageCircle,
  Users
} from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router";

export const DidYouKnowCard = () => {
  const navigate = useNavigate();
  const [currentTip, setCurrentTip] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [fadeClass, setFadeClass] = useState("animate-fade-in");
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const tips = [
    {
      text: "December often brings more repeat customers than usual",
      icon: Calendar,
      route: "/reports/customers/overview",
      emoji: "🎄"
    },
    {
      text: "Checking sales by day helps you spot your strongest days quickly.",
      icon: TrendingUp,
      route: "/reports/sales/overview",
      emoji: "📊"
    },
    {
      text: "A simple thank-you text can bring customers back.",
      icon: MessageCircle,
      route: "/campaigns/broadcast",
      emoji: "💬"
    },
    {
      text: "Holiday shoppers are more likely to return if they hear from you once after their visit",
      icon: Users,
      route: "/campaigns/broadcast",
      emoji: "🧑🏽‍🎄"
    }
  ];

  //   const CurrentIcon = tips[currentTip].icon;

  useEffect(() => {
    // Start auto-rotation
    if (!isHovered) {
      intervalRef.current = setInterval(() => {
        setFadeClass("animate-fade-out");
        setTimeout(() => {
          setCurrentTip((prev) => (prev + 1) % tips.length);
          setFadeClass("animate-fade-in");
        }, 300);
      }, 5500);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isHovered, tips.length]);

  const handleClick = () => {
    navigate(tips[currentTip].route);
  };

  const handleDotClick = (index: number) => {
    setFadeClass("animate-fade-out");
    setTimeout(() => {
      setCurrentTip(index);
      setFadeClass("animate-fade-in");
    }, 300);
  };

  return (
    <div
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="group relative px-5 py-3.5 rounded-[18px] bg-gradient-to-br from-[#F0FBF8] via-white to-[#FFFEF5] backdrop-blur-sm border border-primary/10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgba(0,150,136,0.15)] transition-all duration-300 hover:scale-[1.02] cursor-pointer overflow-hidden"
    >
      {/* Decorative background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-4 right-4 w-24 h-24 rounded-full bg-primary/20 blur-2xl" />
        <div className="absolute bottom-4 left-4 w-32 h-32 rounded-full bg-[#FFD166]/20 blur-3xl" />
      </div>

      {/* Hover glow effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-[#FFD166]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

      <div className="relative flex flex-col h-full">
        <div className="flex items-center gap-2 mb-2">
          <Lightbulb className="w-5 h-5 text-primary animate-pulse" />
          <h3 className="text-[16px] font-bold text-primary">Did You Know?</h3>
        </div>

        <div className="flex-1 flex items-center mb-2">
          <div className={`flex items-start gap-3 ${fadeClass}`}>
            <span className="text-2xl flex-shrink-0 mt-0.5">
              {tips[currentTip].emoji}
            </span>
            <p className="text-[14px] text-foreground leading-relaxed font-medium">
              {tips[currentTip].text}
            </p>
          </div>
        </div>

        {/* Pagination dots */}
        <div className="flex items-center justify-center gap-2 mt-auto">
          {tips.map((_, index) => (
            <button
              key={index}
              onClick={(e) => {
                e.stopPropagation();
                handleDotClick(index);
              }}
              className={`transition-all duration-300 rounded-full ${
                index === currentTip
                  ? "w-6 h-2 bg-primary"
                  : "w-2 h-2 bg-primary/30 hover:bg-primary/50"
              }`}
              aria-label={`Go to tip ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
